import { handleError } from 'features/notification/store';
import { sendAnalyticsInfo } from 'features/analytics/helpers';
import type {
  Participant,
  VoiceSessionResponse,
} from 'features/voice-session/interfaces';
import { VoiceSessionDaoService } from 'features/voice-session/services';

export type AstSignMailAgentRemovalStatus =
  | 'removed'
  | 'missing-session'
  | 'missing-agent-participant'
  | 'lookup-failed'
  | 'delete-failed';

const getCurrentAgentParticipant = (
  participants: Participant[],
  agentNumber: string
) =>
  participants.find(
    (participant) =>
      participant.type === 'agent' &&
      participant.agentId === agentNumber &&
      participant.state !== 'disconnected'
  );

export const removeCurrentAgentFromVoiceSession = async ({
  voiceSessionId,
  agentNumber,
  dispatch,
}: {
  voiceSessionId: string;
  agentNumber: string;
  dispatch: (
    action:
      | ReturnType<typeof handleError>
      | ReturnType<typeof sendAnalyticsInfo>
  ) => unknown;
}): Promise<AstSignMailAgentRemovalStatus> => {
  if (!voiceSessionId) {
    return 'missing-session';
  }

  let voiceSession: VoiceSessionResponse;
  try {
    voiceSession =
      await VoiceSessionDaoService.getVoiceSessionById(voiceSessionId);
  } catch (error) {
    dispatch(
      handleError({
        error: error as Error,
        methodName: 'removeCurrentAgentFromVoiceSession',
        disableUserNotification: true,
      })
    );
    return 'lookup-failed';
  }

  const currentAgentParticipant = getCurrentAgentParticipant(
    voiceSession.participants,
    agentNumber
  );

  if (!currentAgentParticipant) {
    return 'missing-agent-participant';
  }

  try {
    await VoiceSessionDaoService.deleteParticipant({
      sessionId: voiceSessionId,
      participantId: currentAgentParticipant.id,
      keepAlive: true,
    });
    dispatch(
      sendAnalyticsInfo({
        Method: 'removeCurrentAgentFromVoiceSession',
        message:
          'Current agent participant removed from voice session during AST SignMail handoff.',
        LogGroup: 'AstSignMail',
      })
    );
    return 'removed';
  } catch (error) {
    dispatch(
      handleError({
        error: error as Error,
        methodName: 'removeCurrentAgentFromVoiceSession',
        disableUserNotification: true,
      })
    );
    return 'delete-failed';
  }
};
